<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useMediaQuery } from '@vueuse/core'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormAlert,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from '@/shared/ui'
import { MasterServiceForm } from '@/entities/master-service'
import type { MasterServiceI18nKey } from '@/entities/master-service'
import { useMasterServices } from '../model/useMasterServices'
import MasterServiceRow from './MasterServiceRow.vue'

const props = defineProps<{ masterId: string }>()

const emit = defineEmits<{ notFound: [] }>()

const { t } = useI18n()

const {
  listStatus,
  assigned,
  available,
  catalogEmpty,
  politeMessage,
  form,
  detach,
  load,
  openAdd,
  openEdit,
  closeForm,
  submitForm,
  requestDetach,
  cancelDetach,
  confirmDetach,
} = useMasterServices(props.masterId, {
  onNotFound: () => emit('notFound'),
})

const editingServiceName = computed<string | undefined>(() => {
  if (form.mode !== 'edit' || !form.serviceId) return undefined
  return assigned.value.find(r => r.serviceId === form.serviceId)?.name
})

const isDesktop = useMediaQuery('(min-width: 768px)')

onMounted(load)
</script>

<template>
  <div>
    <!-- Loading -->
    <div
      v-if="listStatus === 'loading'"
      aria-busy="true"
      aria-live="polite"
      class="flex flex-col gap-2 p-4"
    >
      <span class="sr-only">{{ t('masters.services.loading') }}</span>
      <div
        v-for="n in 3"
        :key="n"
        data-testid="skeleton-row"
        class="flex items-center gap-2 py-2"
      >
        <Skeleton class="h-4 flex-1 rounded" />
        <Skeleton class="size-8 rounded-md" />
        <Skeleton class="size-8 rounded-md" />
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="listStatus === 'error'" class="flex flex-col gap-3 p-4">
      <FormAlert
        data-testid="list-error"
        variant="destructive"
        :message="t('masters.services.listError')"
      />
      <Button
        data-testid="btn-retry"
        variant="outline"
        size="sm"
        @click="load()"
      >
        {{ t('masters.services.retry') }}
      </Button>
    </div>

    <!-- Ready -->
    <template v-else-if="listStatus === 'ready'">
      <!-- Empty: catalog has no services yet -->
      <Card v-if="catalogEmpty && assigned.length === 0" data-testid="empty-catalog" class="m-4">
        <CardHeader>
          <CardTitle>{{ t('masters.services.emptyCatalog.title') }}</CardTitle>
          <CardDescription>{{ t('masters.services.emptyCatalog.body') }}</CardDescription>
        </CardHeader>
        <CardContent>
          <RouterLink to="/services">
            <Button variant="outline" size="sm">{{ t('masters.services.emptyCatalog.cta') }}</Button>
          </RouterLink>
        </CardContent>
      </Card>

      <!-- Empty: catalog exists but none assigned yet -->
      <Card v-else-if="assigned.length === 0" data-testid="empty-state" class="m-4">
        <CardHeader>
          <CardTitle>{{ t('masters.services.empty.title') }}</CardTitle>
          <CardDescription>{{ t('masters.services.empty.body') }}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button size="sm" data-testid="btn-add" @click="openAdd()">
            {{ t('masters.services.empty.cta') }}
          </Button>
        </CardContent>
      </Card>

      <!-- List with rows -->
      <div v-else class="flex flex-col">
        <div class="flex items-center justify-between px-4 pb-2 pt-4">
          <span class="text-sm font-medium text-foreground">
            {{ t('masters.services.add') }}
          </span>
          <Button size="sm" data-testid="btn-add" @click="openAdd()">
            {{ t('masters.services.add') }}
          </Button>
        </div>
        <div data-testid="service-list" class="divide-y divide-border px-4">
          <MasterServiceRow
            v-for="row in assigned"
            :key="row.serviceId"
            :row="row"
            @edit="openEdit($event)"
            @detach="requestDetach($event)"
          />
        </div>
      </div>
    </template>

    <!-- Live region for a11y announcements (always in DOM — live regions must pre-exist the message) -->
    <p role="status" aria-live="polite" aria-atomic="true" class="sr-only">{{ politeMessage }}</p>

    <!-- Add/Edit Sheet -->
    <Sheet :open="form.open" @update:open="(v) => { if (!v && form.status !== 'saving') closeForm() }">
      <SheetContent :side="isDesktop ? 'right' : 'bottom'" class="h-[90dvh] overflow-y-auto md:h-full">
        <SheetHeader>
          <SheetTitle>
            {{ form.mode === 'add' ? t('masters.services.form.addTitle') : t('masters.services.form.editTitle') }}
          </SheetTitle>
        </SheetHeader>
        <MasterServiceForm
          :mode="form.mode"
          :service-id="form.serviceId"
          :service-name="editingServiceName"
          :available="available"
          :duration="form.duration"
          :price="form.price"
          :buffer="form.buffer"
          :status="form.status"
          :field-errors="form.fieldErrors"
          :form-error="form.formError"
          @update:service-id="form.serviceId = $event"
          @update:duration="form.duration = $event"
          @update:price="form.price = $event"
          @update:buffer="form.buffer = $event"
          @submit="submitForm()"
          @cancel="closeForm()"
        />
      </SheetContent>
    </Sheet>

    <!-- Detach confirm AlertDialog -->
    <AlertDialog
      :open="detach.open"
      @update:open="(v) => { if (!v && detach.status !== 'removing') cancelDetach() }"
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ t('masters.services.detach.title') }}</AlertDialogTitle>
          <AlertDialogDescription>{{ t('masters.services.detach.description') }}</AlertDialogDescription>
        </AlertDialogHeader>
        <FormAlert
          v-if="detach.error"
          variant="destructive"
          :message="t(detach.error as MasterServiceI18nKey)"
        />
        <AlertDialogFooter>
          <AlertDialogCancel @click="cancelDetach()">
            {{ t('masters.services.actions.cancel') }}
          </AlertDialogCancel>
          <AlertDialogAction
            :loading="detach.status === 'removing'"
            variant="destructive"
            @click="confirmDetach()"
          >
            {{ t('masters.services.detach.confirm') }}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>
